/**
 * KYC API response parsers
 * Transform API responses to app-friendly format
 */

/**
 * Parse KYC error response
 * @param {Object} error - Raw error response
 * @returns {Object} Parsed error data
 */
export const parseKycErrorResponse = (error) => {
  if (!error) {
    return {
      success: false,
      message: 'Unknown error occurred',
      code: 'UNKNOWN_ERROR',
      details: null
    };
  }

  const { data, status } = error;

  if (data?.code) {
    return {
      success: false,
      code: data.code,
      message: data.message || 'An error occurred',
      details: data.details || null
    };
  }

  return {
    success: false,
    message: data?.message || data?.error || 'An error occurred',
    code: data?.code || `HTTP_${status}`,
    details: data?.details || null,
    status: status || 500
  };
};

/**
 * Parse initiate KYC response
 * @param {Object} response - Raw API response
 * @returns {Object} Parsed KYC initiation data
 */
export const parseInitiateKycResponse = (response) => {
  if (!response || !response.result) {
    return {
      success: false,
      message: response?.message || 'Failed to initiate KYC',
      code: response?.code || 'UNKNOWN_ERROR',
      data: null
    };
  }

  const { result } = response;

  // Handle Validated Case (Structure 1)
  if (result.status === "VALIDATED") {
    return {
      success: true,
      status: result.status,
      remark: result.remark,
      kycMasterId: result.kycMasterId,
      kraStatus: result.kraStatus ? {
        panNumber: result.kraStatus.pan_number,
        name: result.kraStatus.name,
        statusCode: result.kraStatus.status_code,
        status: result.kraStatus.status,
        statusDescription: result.kraStatus.status_description,
        kraStatusCode: result.kraStatus.kra_status_code,
        statusDate: result.kraStatus.status_date,
        entryDate: result.kraStatus.entry_date,
        modificationDate: result.kraStatus.modification_date,
        updateStatus: result.kraStatus.update_status,
        kycMode: result.kraStatus.kyc_mode,
        kycModeDescription: result.kraStatus.kyc_mode_description,
        ipvFlag: result.kraStatus.ipv_flag,
        responseDate: result.kraStatus.response_date,
        totalRecords: result.kraStatus.total_records,
        refId: result.kraStatus.ref_id
      } : null,
      kraDetails: result.kraDetails ? {
        applicationType: result.kraDetails.application_type,
        applicationNumber: result.kraDetails.application_number,
        panCopy: result.kraDetails.pan_copy,
        exemptStatus: result.kraDetails.exempt_status,
        exemptIdProof: result.kraDetails.exempt_id_proof,
        personalInformation: result.kraDetails.personal_information ? {
          panNumber: result.kraDetails.personal_information.pan_number,
          name: result.kraDetails.personal_information.name,
          dob: result.kraDetails.personal_information.dob,
          gender: result.kraDetails.personal_information.gender,
          fatherName: result.kraDetails.personal_information.father_name,
          nationality: result.kraDetails.personal_information.nationality,
          residentialStatus: result.kraDetails.personal_information.residential_status,
          uidNumber: result.kraDetails.personal_information.uid_number
        } : null,
        contactInformation: result.kraDetails.contact_information ? {
          mobileNumber: result.kraDetails.contact_information.mobile_number,
          emailAddress: result.kraDetails.contact_information.email_address,
          correspondenceAddress: result.kraDetails.contact_information.correspondence_address ? {
            addressLine1: result.kraDetails.contact_information.correspondence_address.address_line1,
            addressLine2: result.kraDetails.contact_information.correspondence_address.address_line2,
            addressLine3: result.kraDetails.contact_information.correspondence_address.address_line3,
            city: result.kraDetails.contact_information.correspondence_address.city,
            state: result.kraDetails.contact_information.correspondence_address.state,
            country: result.kraDetails.contact_information.correspondence_address.country,
            pinCode: result.kraDetails.contact_information.correspondence_address.pin_code,
            addressProof: result.kraDetails.contact_information.correspondence_address.address_proof,
            addressProofDate: result.kraDetails.contact_information.correspondence_address.address_proof_date
          } : null,
          permanentAddress: result.kraDetails.contact_information.permanent_address ? {
            addressLine1: result.kraDetails.contact_information.permanent_address.address_line1,
            addressLine2: result.kraDetails.contact_information.permanent_address.address_line2,
            addressLine3: result.kraDetails.contact_information.permanent_address.address_line3,
            city: result.kraDetails.contact_information.permanent_address.city,
            state: result.kraDetails.contact_information.permanent_address.state,
            country: result.kraDetails.contact_information.permanent_address.country,
            pinCode: result.kraDetails.contact_information.permanent_address.pin_code,
            addressProof: result.kraDetails.contact_information.permanent_address.address_proof,
            addressProofDate: result.kraDetails.contact_information.permanent_address.address_proof_date
          } : null
        } : null,
        financialInformation: result.kraDetails.financial_information ? {
          income: result.kraDetails.financial_information.income,
          occupation: result.kraDetails.financial_information.occupation,
          politicalConnection: result.kraDetails.financial_information.political_connection,
          netWorthDate: result.kraDetails.financial_information.net_worth_date
        } : null,
        requestTracingDetails: result.kraDetails.request_tracing_details ? {
          applicationDate: result.kraDetails.request_tracing_details.application_date,
          commencementDate: result.kraDetails.request_tracing_details.commencement_date,
          downloadDate: result.kraDetails.request_tracing_details.download_date,
          dataDumpType: result.kraDetails.request_tracing_details.data_dump_type
        } : null,
        fatcaDetails: result.kraDetails.fatca_details ? {
          applicable: result.kraDetails.fatca_details.applicable,
          dateDeclaration: result.kraDetails.fatca_details.date_declaration
        } : null,
        kraInformation: result.kraDetails.kra_information ? {
          kraInfo: result.kraDetails.kra_information.kra_info,
          requestDate: result.kraDetails.kra_information.request_date,
          responseDate: result.kraDetails.kra_information.response_date,
          totalRecords: result.kraDetails.kra_information.total_records
        } : null,
        kycInformation: result.kraDetails.kyc_information ? {
          kycMode: result.kraDetails.kyc_information.kyc_mode,
          kycModeDescription: result.kraDetails.kyc_information.kyc_mode_description,
          ipvFlag: result.kraDetails.kyc_information.ipv_flag,
          ipvDate: result.kraDetails.kyc_information.ipv_date,
          status: result.kraDetails.kyc_information.status,
          statusCode: result.kraDetails.kyc_information.status_code,
          statusDescription: result.kraDetails.kyc_information.status_description,
          statusDate: result.kraDetails.kyc_information.status_date,
          documentProof: result.kraDetails.kyc_information.document_proof,
          internalReference: result.kraDetails.kyc_information.internal_reference,
          branchCode: result.kraDetails.kyc_information.branch_code,
          maritalStatus: result.kraDetails.kyc_information.marital_status
        } : null,
        errorDetails: result.kraDetails.error_details ? {
          errorDescription: result.kraDetails.error_details.error_description
        } : null,
        refId: result.kraDetails.ref_id
      } : null,
      workflowDetails: result.workflowDetails ? {
        customerIdentifier: result.workflowDetails.customerIdentifier,
        kid: result.workflowDetails.kid,
        accessToken: result.workflowDetails.accessToken ? {
          entityId: result.workflowDetails.accessToken.entity_id,
          id: result.workflowDetails.accessToken.id,
          validTill: result.workflowDetails.accessToken.valid_till,
          createdAt: result.workflowDetails.accessToken.created_at
        } : null,
        referenceId: result.workflowDetails.referenceId,
        transactionId: result.workflowDetails.transactionId
      } : null,
      requestId: response.requestId
    };
  }

  // Handle Not Found / Initiation Case (Structure 2)
  return {
    success: true,
    status: result?.status, // "NOT FOUND"
    remark: result?.remark,
    kycMasterId: result?.kycMasterId,
    referenceId: result?.referenceId,
    workflowDetails: result?.workflowDetails ? {
      customerIdentifier: result?.workflowDetails?.customerIdentifier,
      kid: result?.workflowDetails?.kid,
      accessToken: result?.workflowDetails?.accessToken ? {
        entityId: result?.workflowDetails?.accessToken?.entity_id,
        id: result?.workflowDetails?.accessToken?.id,
        validTill: result?.workflowDetails?.accessToken?.valid_till,
        createdAt: result?.workflowDetails?.accessToken?.created_at
      } : null
    } : null,
    // Mapped for backward compatibility or easier access if needed
    customerIdentifier: result?.workflowDetails?.customerIdentifier || result?.customerIdentifier,
    accessToken: result?.workflowDetails?.accessToken ? {
      entityId: result?.workflowDetails?.accessToken?.entity_id,
      id: result?.workflowDetails?.accessToken?.id,
      validTill: result?.workflowDetails?.accessToken?.valid_till,
      createdAt: result?.workflowDetails?.accessToken?.created_at
    } : null
  };
};

/**
 * Parse KRA details response
 * @param {Object} response - Raw API response
 * @returns {Object} Parsed KRA details
 */
export const parseKraDetailsResponse = (response) => {
  if (!response || response.code !== 'KYCS0003' || !response?.result) {
    return {
      success: false,
      message: response?.message || 'Failed to fetch KRA details',
      code: response?.code || 'UNKNOWN_ERROR',
      data: null
    };
  }

  const { result } = response;
  const { kraDetails } = result;

  return {
    success: true,
    message: response?.message,
    kycMasterId: result?.kycMasterId,
    kraDetails: kraDetails ? {
      refId: kraDetails?.ref_id,
      applicationType: kraDetails?.application_type,
      applicationNumber: kraDetails?.application_number,
      personalInformation: kraDetails?.personal_information ? {
        panNumber: kraDetails?.personal_information?.pan_number,
        name: kraDetails?.personal_information?.name,
        dob: kraDetails?.personal_information?.dob,
        gender: kraDetails?.personal_information?.gender,
        fatherName: kraDetails?.personal_information?.father_name
      } : null,
      contactInformation: kraDetails?.contact_information ? {
        mobileNumber: kraDetails?.contact_information?.mobile_number,
        emailAddress: kraDetails?.contact_information?.email_address,
        correspondenceAddress: kraDetails?.contact_information?.correspondence_address ? {
          addressLine1: kraDetails?.contact_information?.correspondence_address?.address_line1,
          city: kraDetails?.contact_information?.correspondence_address?.city,
          state: kraDetails?.contact_information?.correspondence_address?.state,
          pinCode: kraDetails?.contact_information?.correspondence_address?.pin_code
        } : null
      } : null,
      kycInformation: kraDetails?.kyc_information ? {
        status: kraDetails?.kyc_information?.status,
        statusCode: kraDetails?.kyc_information?.status_code,
        statusDescription: kraDetails?.kyc_information?.status_description
      } : null
    } : null,
    requestId: response?.requestId
  };
};

/**
 * Parse KYC completion response
 * @param {Object} response - Raw API response
 * @returns {Object} Parsed completion data
 */
export const parseCompleteKycResponse = (response) => {
  if (!response || response.code !== 'KYCS0006' || !response.result) {
    return {
      success: false,
      message: response?.message || 'Failed to complete KYC',
      code: response?.code || 'UNKNOWN_ERROR',
      data: null
    };
  }

  const { result } = response;

  return {
    success: true,
    message: response.message,
    code: response.code,
    kycMasterId: result.kycMasterId,
    kycStatus: result.kycStatus,
    updatedAt: result.updatedAt,
    requestId: response.requestId
  };
};
